import axios, {AxiosRequestConfig, CancelTokenSource, RawAxiosRequestHeaders} from "axios";
import {DevError, DevLog} from "./commonFunctions/devLog";
import {deepCopy} from "./commonFunctions/jsonObjManipulation";
import {fetchRequest, FetchRequestStruct} from "./types/IAPI";
import {IAuthContextConfig} from "./types/IAuth";

//Convert js objects to url Params if using GET
export function convertDataToParams (dataPre: any): string {
	if(typeof dataPre === "string" || typeof dataPre.getMonth === "function" ) {
		DevError("Invalid object passed to api! If get body passed, must be an object with params.");
		return "";
	}

	const data = JSON.parse(JSON.stringify(dataPre)); //> prevents changes to original data
	let getParams= "?";
	Object.keys(data).forEach((e) => {
		if(!!data[e] && typeof data[e].getMonth === "function"){
			data[e] = data[e].toUTCString();
		}

		if(!!data[e] && Array.isArray(data[e])){
			let arrTxt = "";
			data[e].forEach((entry: any, i: number) => {
				arrTxt += (i === 0 ? "" : e + "=") + entry + "&";
			});

			data[e] = arrTxt.substring(0, arrTxt.length - 1);
		}

		getParams += e + "=" + data[e] + "&";
	});

	return getParams.substring(0, getParams.length -1);
}


function GenerateAxiosRequest<R>(request: FetchRequestStruct<R>, config: IAuthContextConfig, jwt?: string): AxiosRequestConfig {
	const baseAPISrc = config.baseApiUrl;
	let axiosRequest: AxiosRequestConfig = {withCredentials: true};
	if(!request || !(typeof request === "string" || !!request.url))
		throw new Error("Invalid request! No path provided");

	if(baseAPISrc === undefined || baseAPISrc === null)
		throw new Error("Failed to retrieve base Api Path");

	if(typeof request === "string"){
		axiosRequest = {url: baseAPISrc + request};
	}
	else {
		axiosRequest.url = (baseAPISrc + request.url);
		axiosRequest.method = request.method ? request.method : "get";

		//set data
		if(request.data){
			if(axiosRequest.method.toLocaleLowerCase() === "get"){
				axiosRequest.url += convertDataToParams(request.data);
			}
			else {
				axiosRequest.data = request.data;
			}
		}
	}

	//set headers
	let configHeaders: RawAxiosRequestHeaders = {
		"Accept": "application/json",
		"Content-Type": "application/json"
	};

	if(jwt)
		configHeaders["Authorization"] = `Bearer ${jwt}`;

	if(request.config?.headers){
		configHeaders = {
			...configHeaders,
			...request.config.headers
		};
		delete request["config"]["headers"];
	}

	//set remaining request config, overriding any defaults set using provided fetch request
	axiosRequest = {
		...axiosRequest,
		headers: configHeaders,
		...request.config
	};
	return axiosRequest;
}

function concreteFetchRequest<R>(request: fetchRequest<R>): FetchRequestStruct<R>{
	if(typeof request === "string"){
		request = {
			url: request
		} as fetchRequest<R>;
	}
	return request as FetchRequestStruct<R>;
}

export function hitApi<S = any, R = any>(requestInput: fetchRequest<R>, config: IAuthContextConfig, jwt?: string, cancelToken?: CancelTokenSource): Promise<S>{

	if(!cancelToken){
		cancelToken = axios.CancelToken.source();
	}
	const request = concreteFetchRequest(requestInput);

	// eslint-disable-next-line no-async-promise-executor
	return new Promise<S>(async (resolutionFunc, rejectionFunc) => {

		try {
			const axiosRequest = GenerateAxiosRequest(request, config, jwt);

			axiosRequest.cancelToken = cancelToken?.token;

			if(!axiosRequest.cancelToken) return;

			let callback: Function | null = null;
			if(request.callback){
				callback = request.callback.bind({});
			}

			DevLog("DATAAPI CALL -> ", axiosRequest);

			let result = await axios(axiosRequest as AxiosRequestConfig);
			if (!axios.isCancel(result)) {

				DevLog("Raw api response: " + axiosRequest.url, result);

				if(result?.headers["content-type"]?.includes("application/json")){
					result.data = deepCopy(result.data, true);
				}

				if(callback){
					//checking response removes the need to return response in callback if untouched
					const callbackResponse = callback(result);
					if(callbackResponse){
						result = callbackResponse;
					}
				}

				//Type Check
				if(result.data as S === null){
					throw new Error("API: Failed Type Check!");
				}

				resolutionFunc(result.data);
			}
		} catch (error) {
			if (!axios.isCancel(error)) {
				// eslint-disable-next-line no-console
				console.error("API -> ", error);
				rejectionFunc(error);
			}
		}
	});
}

export default hitApi;